import { FunctionComponent } from 'react'
import DromoUploader from 'dromo-uploader-react'
import {
  IReviewStepData,
  IRowHookInput,
  IRowHookOutputInternal,
  IDeveloperField,
  ITableMessage,
  IBeforeFinishOutput,
} from 'dromo-uploader-js'
import {
  IRowToAdd,
  IPublicConnectionMethods,
  IPositionSpec,
} from 'dromo-uploader-js/dist/interfaces'
import {
  validations,
  fieldSchema,
  systemSizeSchema,
  soldSchema,
  getSoldToSchema,
} from './FieldSchema'
import { IAuthUser, useIDToken } from '../../state/modules/auth'
import { useImpersonatingUserOrUser } from '../../state/modules/auth'
import {
  requestLimit,
  geocodingRequestsPerMinuteLimit,
  SolarLeaderboardSiteSource,
  SiteListType,
} from '@black-bear-energy/black-bear-energy-common'
import { DromoSiteRecord } from 'common/types'
import { DuplicateCheckResult } from '../helpers/DromoUploader/Deduplication.consts'
import { getAPI } from '../../utils/api'
import { addColumnsIfNeeded } from '../helpers/DromoUploader/addColumnsIfNeeded'
import { updateCellMessages } from '../helpers/DromoUploader/updateCellMessages'
import { addZipCodeLeadingZeros } from '../helpers/DromoUploader/zipCodeValidation'
import { parseDateValues } from '../helpers/DromoUploader/dateValidation'
import { convertZeroToNull } from '../helpers/DromoUploader/convertZeroToNull'
import { validateNumbers } from '../helpers/DromoUploader/validateNumbers'
import { getUploadSummary } from '../helpers/DromoUploader/getUploadSummary'
import { checkForDuplicates } from '../helpers/SolarLeaderboardUpload/checkForDuplicates'
import { getSiteUploadRecordList } from 'components/helpers/SolarLeaderboardUpload/getSiteUploadRecordList'
import {
  chunkSiteListUpload,
  UpsertCounts,
} from '../helpers/DromoUploader/chunkSiteListUpload'
import { handleBulkProcessing } from '../helpers/SolarLeaderboardUpload/HandleBulkProcessing'
import { isProductionHost } from '../../utils/enviroment'
import { longSchema } from '../helpers/DromoUploader/commonFieldSchemas'
import { REIT } from '../../state/modules/reits'

interface SolarLeaderboardUploaderProps {
  reitList: REIT[]
  source: keyof typeof SolarLeaderboardSiteSource
  isOpen: boolean
  onClose: (counts?: UpsertCounts) => void
  onUploadStart: () => void
  onUploadError: () => void
  reitId?: number
  sourceOther?: string
}

export const SolarLeaderboardUploader: FunctionComponent<
  SolarLeaderboardUploaderProps
> = (props) => {
  const {
    reitList,
    source,
    isOpen,
    onClose,
    onUploadStart,
    onUploadError,
    reitId,
    sourceOther,
  } = props
  const user = useImpersonatingUserOrUser() as IAuthUser
  const accessToken = useIDToken()
  const soldToSchema = getSoldToSchema(reitList, reitId)
  let missingDataInsertRows: (IRowToAdd & { rowId: string })[]

  async function beforeUpload(
    sites: DromoSiteRecord[],
    instance: IPublicConnectionMethods,
    reitId?: number,
    accessToken?: string
  ): Promise<IBeforeFinishOutput> {
    if (!reitId) {
      return { cancel: true, message: 'No REIT selected' }
    }

    const api = getAPI(accessToken)

    let duplicatesResult: DuplicateCheckResult | null
    try {
      duplicatesResult = await checkForDuplicates(sites, reitId, api)
    } catch (err) {
      return {
        cancel: true,
        message:
          'An error was thrown while deduplicating records. Please contact support.',
      }
    }

    if (duplicatesResult === null) {
      // no duplicates - show upload summary
      const siteActions = sites.map((site) => site.siteAction)
      const didConfirm = confirm(getUploadSummary(siteActions))
      if (didConfirm) {
        return
      }
      return {
        cancel: true,
        message: '',
      }
    }

    // move duplicate rows to the top of the table
    await instance.removeRows(duplicatesResult.rowIdsToRemove)
    await instance.addRows(duplicatesResult.rowsToAdd)

    return {
      cancel: true,
      message: `${duplicatesResult.dupCount} record${
        duplicatesResult.dupCount > 1 ? 's are duplicates' : ' is a duplicate'
      } of other records. Please review and either update the records to be unique or choose Ignore as the site action.`,
    }
  }

  async function onBulkRowHook(
    records: IRowHookInput[],
    mode: 'init' | 'update'
  ): Promise<IRowHookOutputInternal[]> {
    if (!reitId) {
      throw new Error('No REIT selected')
    }

    const api = getAPI(accessToken)
    const isUpdate = mode === 'update'

    const { missingDataRows, updatedRows } = await handleBulkProcessing({
      records,
      isUpdate,
      reitId,
      api,
    })
    missingDataInsertRows = missingDataRows
    return updatedRows
  }

  async function addRequiredFields(
    instance: IPublicConnectionMethods,
    previewData: IReviewStepData
  ) {
    const headers = Object.values(previewData.headerMapping)
    // system size is always required
    const additionalFields: {
      schema: IDeveloperField
      position: IPositionSpec
    }[] = [{ schema: systemSizeSchema, position: { after: longSchema.key } }]
    // If "sold" is mapped but "sold to" is not, add "sold to".
    // Unfortunately we can't just use the `require_with` validators for these fields
    // because Dromo considers an unchecked checkbox as "filled", which is not the behavior we want
    if (
      headers.includes(soldSchema.key) &&
      !headers.includes(soldToSchema.key)
    ) {
      additionalFields.push({
        schema: soldToSchema,
        position: { after: soldSchema.key },
      })
    }
    // if "sold to" is mapped but "sold" is not, add "sold"
    if (
      headers.includes(soldToSchema.key) &&
      !headers.includes(soldSchema.key)
    ) {
      additionalFields.push({
        schema: soldSchema,
        position: { before: soldToSchema.key },
      })
    }
    await addColumnsIfNeeded(instance, previewData, additionalFields)
  }

  async function uploadSiteListData(
    data: DromoSiteRecord[],
    source: keyof typeof SolarLeaderboardSiteSource,
    reitId?: number,
    sourceOther?: string
  ): Promise<void> {
    if (!reitId) {
      throw new Error('No REIT selected')
    }
    if (data.length === 0) {
      onClose()
    }

    onUploadStart()

    const api = getAPI(accessToken)
    const sites = getSiteUploadRecordList(data, source, reitId, sourceOther)

    let counts: UpsertCounts

    try {
      counts = await chunkSiteListUpload(
        sites,
        SiteListType.New,
        '/solar-leaderboard-sites',
        api
      )
    } catch (error) {
      onUploadError()
      return
    }

    onClose(counts)
  }

  return (
    <DromoUploader
      licenseKey={process.env.REACT_APP_DROMO_FRONTEND_API_KEY!}
      fields={[...fieldSchema, soldToSchema]}
      settings={{
        importIdentifier: 'Solar Leaderboard Uploader',
        title: 'Solar Leaderboard Uploader',
        developmentMode: !isProductionHost(),
        maxFileSize: requestLimit,
        maxRecords: geocodingRequestsPerMinuteLimit,
        manualInputDisabled: true,
        reviewStep: {
          processingText:
            'Geocoding addresses and comparing new sites with existing sites. This may take a while.',
          helpText:
            'Only rows without errors will be uploaded. Hover over a colored cell to see more information. If some errors cannot be resolved, click the "Export" button to download "Only rows with errors" and review this list with the client. Sites that are potentially sold are pulled from the database and added to the bottom of the sheet as new rows.',
        },
      }}
      user={{
        id: user.id ?? '',
        name: user.name,
        email: user.email,
        companyId:
          user.relatedEntity?.toString() ??
          user.relatedChannelPartner?.toString() ??
          '',
      }}
      columnHooks={[
        {
          fieldName: 'zipCode',
          callback: (values) => addZipCodeLeadingZeros(values),
        },
        {
          fieldName: 'onlineDate',
          callback: (values) => parseDateValues(values, true),
        },
        {
          fieldName: 'systemSize',
          callback: (values) => convertZeroToNull(values),
        },
        {
          fieldName: 'latitude',
          callback: (values) => validateNumbers(values),
        },
        {
          fieldName: 'longitude',
          callback: (values) => validateNumbers(values),
        },
      ]}
      stepHooks={[
        {
          type: 'REVIEW_STEP',
          callback: (instance, previewData) =>
            addRequiredFields(instance, previewData as IReviewStepData),
        },
        {
          type: 'REVIEW_STEP_POST_HOOKS',
          callback: async (instance) => {
            // remove rows from current positions
            await instance.removeRows(
              missingDataInsertRows.map((row) => row.rowId)
            )
            // generate and add the siteAction warning messages
            const missingDataRowsWithWarnings = missingDataInsertRows.map(
              (insertRow) => {
                const rowWithWarnings = updateCellMessages(
                  insertRow as IRowHookInput,
                  validations
                )
                insertRow.row.siteAction.info = rowWithWarnings.row.siteAction
                  .info as ITableMessage[]
                return insertRow
              }
            )
            // add the rows (with site action warnings) to the top of the table
            await instance.addRows(missingDataRowsWithWarnings)
          },
        },
      ]}
      rowHooks={[(record) => updateCellMessages(record, validations)]}
      bulkRowHooks={[(records, mode) => onBulkRowHook(records, mode)]}
      beforeFinish={(records, metadata, instance) =>
        beforeUpload(
          records as DromoSiteRecord[],
          instance,
          reitId,
          accessToken
        )
      }
      onResults={(response: DromoSiteRecord[]) => {
        void uploadSiteListData(response, source, reitId, sourceOther)
      }}
      open={isOpen}
      onCancel={onClose}
    ></DromoUploader>
  )
}
